// DecompressTextForm.js
import React, { useState, useRef, useCallback } from 'react';
import { Box, TextField, Button, Typography, Alert, Tabs, Tab } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  UploadFile as UploadFileIcon,
  InsertDriveFile as FileIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import pako from 'pako';

function DecompressTextForm() {
  const [tab, setTab] = useState(0);
  const [text, setText] = useState('');
  const [fileContent, setFileContent] = useState('');
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState(false);
  const [dragging, setDragging] = useState(false);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const decompressAndNavigate = useCallback(
    (base64String) => {
      try {
        const trimmed = base64String.trim();
        const compressedBytes = Uint8Array.from(atob(trimmed), (c) => c.charCodeAt(0));
        const utf8Bytes = pako.ungzip(compressedBytes);
        const decodedText = new TextDecoder().decode(utf8Bytes);
        const travelData = JSON.parse(decodedText);
        setError(false);
        navigate('/tabi-jaws/travel', { state: travelData });
      } catch (err) {
        setError(true);
      }
    },
    [navigate]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (tab === 0) {
      decompressAndNavigate(text);
    } else {
      if (!fileContent) {
        setError(true);
        return;
      }
      decompressAndNavigate(fileContent);
    }
  };

  // ファイル読み込み共通処理
  const readFile = useCallback((file) => {
    if (!file) return;
    setFileName(file.name);
    setError(false);
    const reader = new FileReader();
    reader.onload = (event) => {
      setFileContent(event.target.result);
    };
    reader.onerror = () => {
      setError(true);
    };
    reader.readAsText(file);
  }, []);

  const handleFileChange = (e) => {
    readFile(e.target.files[0]);
    e.target.value = '';
  };

  const handleClearFile = () => {
    setFileName('');
    setFileContent('');
    setError(false);
  };

  // Drag & Drop
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) {
        readFile(file);
      }
    },
    [readFile]
  );

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        maxWidth: 600,
        width: '100%',
        mx: 'auto',
        p: 4,
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
        background: 'linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)',
        color: '#fff',
        textAlign: 'center',
        overflow: 'hidden',
      }}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        sx={{ mb: 2, color: '#fff', fontFamily: 'Poppins, sans-serif' }}
      >
        データインポート
      </Typography>

      {/* タブ切り替え */}
      <Tabs
        value={tab}
        onChange={(_, v) => {
          setTab(v);
          setError(false);
        }}
        variant="fullWidth"
        sx={{
          mb: 2,
          backgroundColor: '#ffffff20',
          borderRadius: 2,
          '& .MuiTab-root': { color: '#ffffffa0', fontWeight: 600 },
          '& .Mui-selected': { color: '#fff' },
          '& .MuiTabs-indicator': { backgroundColor: '#fff' },
        }}
      >
        <Tab label="テキスト入力" />
        <Tab label="ファイル選択" />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          エラーが発生しました。{tab === 0 ? '文字列' : 'ファイル'}が正しいか確認してください。
        </Alert>
      )}

      {/* テキスト入力タブ */}
      {tab === 0 && (
        <TextField
          label="テキストを入力してください"
          variant="filled"
          fullWidth
          value={text}
          onChange={(e) => setText(e.target.value)}
          multiline
          minRows={5}
          sx={{
            mb: 2,
            backgroundColor: '#ffffff90',
            borderRadius: 2,
            '& .MuiFilledInput-root': { backgroundColor: 'transparent' },
            '& .MuiFilledInput-underline:before': { borderBottomColor: '#ffffff66' },
            '& .MuiFilledInput-underline:hover:before': { borderBottomColor: '#ffffff' },
            '& .MuiInputLabel-root': { color: '#ffffffa0' },
            '& .MuiInputLabel-root.Mui-focused': { color: '#ffffff' },
          }}
        />
      )}

      {/* ファイル選択タブ */}
      {tab === 1 && (
        <Box sx={{ mb: 2 }}>
          {/* ドロップゾーン */}
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !fileName && fileInputRef.current.click()}
            sx={{
              border: '2px dashed',
              borderColor: dragging ? '#fff' : '#ffffff80',
              borderRadius: 2,
              p: 4,
              cursor: fileName ? 'default' : 'pointer',
              backgroundColor: dragging ? '#ffffff30' : '#ffffff10',
              transition: 'all 0.2s',
              minHeight: 120,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            {fileName ? (
              /* ファイル選択済み */
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  backgroundColor: '#ffffff20',
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                }}
              >
                <FileIcon sx={{ color: '#fff' }} />
                <Typography sx={{ color: '#fff', fontSize: '14px' }}>{fileName}</Typography>
                <CloseIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearFile();
                  }}
                  sx={{
                    color: '#ffffffa0',
                    cursor: 'pointer',
                    fontSize: '18px',
                    '&:hover': { color: '#fff' },
                  }}
                />
              </Box>
            ) : (
              /* 未選択 */
              <>
                <UploadFileIcon sx={{ fontSize: 40, color: '#ffffffa0' }} />
                <Typography sx={{ color: '#ffffffa0', fontSize: '14px' }}>
                  ここにtxtファイルをドラッグ&ドロップ
                </Typography>
                <Typography sx={{ color: '#ffffff60', fontSize: '12px' }}>
                  またはクリックしてファイルを選択
                </Typography>
              </>
            )}
          </Box>

          <input
            type="file"
            accept=".txt"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
        </Box>
      )}

      {/* インポートボタン（共通） */}
      <Button
        type="submit"
        variant="contained"
        fullWidth
        sx={{
          backgroundColor: '#0072ff',
          ':hover': { backgroundColor: '#005bb5' },
        }}
      >
        インポートする
      </Button>
    </Box>
  );
}

export default DecompressTextForm;
